import { forwardRef, Inject, Injectable, Injector } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { NvToastComponent } from '../../../nvps-libraries/design/nv-toast/nv-toast.component';
import { ModalsService } from '../../shared/modals/modals.service';
import { ApiService } from '../../shared/services/api-service/api-service';
import { BackgroundJob } from '../../shared/services/background-job/background-job.service';
import { IDataLoadParams } from '../../shared/typings/interfaces/data-load.interface';
import { LoadAllStudentPaths } from '../actions';
import * as dataLoadsActions from '../actions/data-loads-actions';

@Injectable()
export class DataLoadsEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private backgroundJob: BackgroundJob,
    private snackBar: MatSnackBar,
    @Inject(forwardRef(() => ModalsService)) private modalsService: ModalsService,
    private injector: Injector,
    private store: Store<any>,
  ) {}

  snackBarRef: MatSnackBarRef<NvToastComponent>;

  @Effect()
  loadDataLoads$ = this.actions$.pipe(
    ofType(dataLoadsActions.LOAD_DATA_LOADS),
    switchMap((action: { type: string; payload: any }) => {
      const { schoolId, where } = action.payload;
      return this.apiService.getDataLoads(schoolId, where).pipe(
        switchMap((dataLoads: any) => [new dataLoadsActions.LoadDataLoadsSuccess(dataLoads)]),
        catchError(error => of(new dataLoadsActions.LoadDataLoadsFail(error))),
      );
    }),
  );

  @Effect({ dispatch: false })
  createDataLoad$ = this.actions$.pipe(
    ofType(dataLoadsActions.CREATE_DATA_LOAD),
    switchMap((action: { type: string; payload: IDataLoadParams }) => {
      const { payload } = action;
      const { context } = payload;
      return this.apiService.createDataLoad(payload).pipe(
        take(1),
        tap((response: { data: { createDataLoad: { backgroundJobId: string } } }) => {
          const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
          const jobId = response.data.createDataLoad.backgroundJobId;
          if (jobId) {
            const promise = (this.backgroundJob as any)(jobId).promise();
            backgroundJobSpinner.activateSpinner('Uploading CSV...', promise, null);
            promise.then(() => {
              const toastText =
                context === 'COLLEGE_LIST' ? 'College list uploaded successfully' : 'Gradebook uploaded successfully';
              // re-load student paths after bulk upload (JE)
              this.store.dispatch(new LoadAllStudentPaths({ schoolId: payload.schoolId }));
              this.snackBar.openFromComponent(NvToastComponent, {
                data: {
                  toastText,
                  hasClearButton: false,
                  actionText: null,
                },
                duration: 3000,
              });
            });
          }
        }),
        catchError(err => {
          const data = {
            message:
              'The data in the CSV file could not be applied to all students in the Data Portal. You can retry the upload which will only update data that has not yet been modified in the Data Portal.',
            title: 'Unable to complete CSV upload',
            confirmText: 'Ok',
          };
          this.modalsService.openErrorModal(data);
          return EMPTY;
        }),
      );
    }),
  );
}
